import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode'; 
import Swal from 'sweetalert2';
const AuthContext = createContext();

const REFRESH_INTERVAL_MS = 29 * 60 * 1000; 

export const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(() =>
    localStorage.getItem('authToken') ? JSON.parse(localStorage.getItem('authToken')) : null
  );
  const [user, setUser] = useState(() =>
    localStorage.getItem('authToken') ? jwtDecode(JSON.parse(localStorage.getItem('authToken')).access) : null
  );
  const [loading, setLoading] = useState(true); 
  const navigate = useNavigate();

  const logoutUser = useCallback(() => {
    setAuthToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('access_token'); // Clean up admin token
    navigate('/login');
  }, [navigate]);


  const updateToken = useCallback(async () => {
    if (!authToken?.refresh) {
      console.log("No refresh token available, attempting logout.");
      logoutUser();
      return;
    }

    console.log('Attempting to refresh token');
    try {
      const response = await axios.post('https://vasstrinfo.com/api/token/refresh/', {
        refresh: authToken.refresh,
      });

      if (response.status === 200) {
        const newAuthToken = {
            ...authToken,
            access: response.data.access,
            // Update refresh token if rotation is enabled
            refresh: response.data.refresh || authToken.refresh,
        };
        setAuthToken(newAuthToken);
        setUser(jwtDecode(newAuthToken.access));
        localStorage.setItem('authToken', JSON.stringify(newAuthToken));
        localStorage.setItem('access_token', newAuthToken.access); // For admin API calls
        console.log('Token refreshed successfully');
      } else {
        console.log('Token refresh failed with status:', response.status);
        // Show user-friendly message before logout
        Swal.fire({
          title: 'Session Expired',
          text: 'Your session has expired. Please login again.',
          icon: 'warning',
          confirmButtonText: 'Login'
        }).then(() => {
          logoutUser();
        });
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Show user-friendly message before logout
      Swal.fire({
        title: 'Session Expired',
        text: 'Your session has expired. Please login again.',
        icon: 'warning',
        confirmButtonText: 'Login'
      }).then(() => {
        logoutUser();
      });
    }
  }, [authToken, logoutUser]); 


  const loginUser = async (e) => {
    e.preventDefault();
    const loginData = {
      email: e.target.email.value,
      password: e.target.password.value,
    };

    try {
      const response = await axios.post('https://vasstrinfo.com/api/token/', loginData);
      if (response.status === 200) {
        setAuthToken(response.data);
        setUser(jwtDecode(response.data.access));
        localStorage.setItem('authToken', JSON.stringify(response.data));
        localStorage.setItem('access_token', response.data.access); // For admin API calls
        navigate('/homepage');
      }
    } catch (error) {
      console.error('Login failed:', error);
      let errorMessage = 'Invalid credentials or server error.';
      if (error.response && error.response.data) {
          errorMessage = error.response.data.detail || JSON.stringify(error.response.data);
      } else if (error.message) {
          errorMessage = error.message;
      }
      Swal.fire({
        title: 'Login failed',
        text: `Login failed: ${errorMessage}`,
        icon:"warning"
      })
    }
  };


  useEffect(() => {
    let interval;

    if (authToken) {


      interval = setInterval(() => {
        updateToken();
      }, REFRESH_INTERVAL_MS);
    }

   
    if (loading) {
        setLoading(false);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [authToken, updateToken, loading]); 
  useEffect(() => {
    const storedAuthToken = localStorage.getItem('authToken');
    if (storedAuthToken) {
      try {
        const parsedToken = JSON.parse(storedAuthToken);
        if (parsedToken && parsedToken.access && parsedToken.refresh) {
          const decodedUser = jwtDecode(parsedToken.access);
          if (decodedUser.exp * 1000 < Date.now()) {
            console.log("Initial token from localStorage is expired. Attempting refresh.");
            setAuthToken(parsedToken); 
            (async () => {
                try {
                    const response = await axios.post('https://vasstrinfo.com/api/token/refresh/', {
                        refresh: parsedToken.refresh,
                    });
                    if (response.status === 200) {
                        const newAuthToken = { ...parsedToken, access: response.data.access };
                        setAuthToken(newAuthToken);
                        setUser(jwtDecode(newAuthToken.access));
                        localStorage.setItem('authToken', JSON.stringify(newAuthToken));
                        localStorage.setItem('access_token', newAuthToken.access); // For admin API calls
                        console.log('Initial token refreshed successfully on load');
                    } else {
                        logoutUser(); // Refresh failed
                    }
                } catch (err) {
                    console.error("Initial refresh failed on load", err);
                    logoutUser(); // Refresh failed
                } finally {
                    setLoading(false);
                }
            })();
            return; // Skip setting user from expired token
          } else {
            setAuthToken(parsedToken);
            setUser(decodedUser);
            localStorage.setItem('access_token', parsedToken.access); // For admin API calls
          }
        } else {
          localStorage.removeItem('authToken');
        }
      } catch (error) {
        console.error("Error parsing token from localStorage or token expired:", error);
        localStorage.removeItem('authToken'); 
      }
    }
    setLoading(false); 
  }, [logoutUser]);


  const contextData = {
    user,
    authToken,
    authTokens: authToken, // Add this for compatibility with existing components
    loading,
    loginUser,
    logoutUser,
  };

  return <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>;
};

export default AuthContext;